import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Raised / 3D treatment for FILLED variants (primary, destructive).
 * Keeps the fill color + padding untouched and adds:
 *   • a hairline edge — translucent black so it reads as a slightly darker
 *     border on any hue (blue, red, …),
 *   • an inset top highlight — the lighter top edge that sells the "3D",
 *     plus a faint bottom inner shade for the bevel,
 *   • a soft drop shadow beneath.
 * Hue-agnostic on purpose so every filled button shares one physical feel.
 */
const RAISED_FILLED =
  "border-black/[0.14] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.24),inset_0_-1px_0_0_rgba(16,24,40,0.10),0_1px_2px_0_rgba(16,24,40,0.16)]"

/**
 * Button — Flozy Design System
 *
 * Hierarchy (per README):
 *   primary    → filled Flozy blue                (verb-noun CTAs)
 *   secondary  → white, gray border, ink text     (default neutral)
 *   tertiary   → transparent, hover gray-100      (toolbar / chrome)
 *   destructive→ filled error red                 (tone="error")
 *   link       → text-only blue with underline
 *
 * Aliases kept for back-compat with existing call-sites:
 *   default → primary, outline → secondary, ghost → tertiary,
 *   dark/muted → mapped sensibly.
 *
 * Sizes follow Flozy spec: sm 32h / md 36h / lg 40h, 14/20 medium font.
 */
const buttonVariants = cva(
  [
    // base
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md",
    "text-sm font-medium leading-5 cursor-pointer select-none shrink-0",
    "border border-transparent shadow-xs",
    "transition-[background-color,border-color,box-shadow,color,transform]",
    "duration-100 ease-out active:scale-[0.98]",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    "outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:border-primary",
    "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  ].join(" "),
  {
    variants: {
      variant: {
        // Flozy canonical
        primary:
          "bg-primary text-primary-foreground hover:bg-[var(--primary-hover)] " +
          RAISED_FILLED,
        secondary:
          "bg-background text-foreground border-[var(--border-strong)] hover:bg-muted",
        tertiary:
          "bg-transparent border-transparent shadow-none text-muted-foreground hover:bg-muted hover:text-foreground",
        destructive:
          "bg-destructive text-white hover:brightness-95 focus-visible:ring-destructive/30 " +
          RAISED_FILLED,
        link:
          "bg-transparent border-transparent shadow-none text-primary hover:underline underline-offset-4 px-1",

        // Aliases (back-compat)
        default:
          "bg-primary text-primary-foreground hover:bg-[var(--primary-hover)] " +
          RAISED_FILLED,
        outline:
          "bg-background text-foreground border-[var(--border-strong)] hover:bg-muted",
        ghost:
          // Hover matches the RowAction primitive — soft but clearly visible
          // foreground tint so action triggers (kebab menus, table icons, etc.)
          // share one feel across the platform.
          "bg-transparent border-transparent shadow-none text-muted-foreground hover:bg-foreground/10 hover:text-foreground",
        dark:
          "bg-background text-foreground border-[var(--border-strong)] hover:bg-muted",
        muted: "bg-muted text-foreground border-transparent hover:bg-secondary",
      },
      size: {
        // Flozy default: 36h, 14/20 medium, tight padding (12px h, 6px v; 10h when icon present)
        default: "h-9 px-3 py-1.5 has-[>svg]:px-2.5 rounded-md",
        sm: "h-8 px-2.5 py-1 has-[>svg]:px-2 text-[13px] leading-[18px] rounded-md gap-1",
        lg: "h-10 px-3.5 py-2 has-[>svg]:px-3 rounded-md",
        icon: "size-9 rounded-md",
        "icon-sm": "size-8 rounded-md",
        "icon-lg": "size-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
)

function Button({
  className,
  variant = "primary",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
